/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React from "react"
import { ToolTip } from "./ToolTip"

interface IProps {
  onClick(): void
  icon:
    | "play"
    | "pause"
    | "speaker"
    | "speaker-muted"
    | "settings"
    | "captions"
    | "captions-off"
    | "fullscreen"
    | "exit-fullscreen"
  tooltip: { text: string; placement: "top" | "top-start" | "top-end" }
  displayToolTips?: boolean
}
export function PlayerButton({ onClick, icon, tooltip, displayToolTips }: IProps) {
  function isTouchDevice() {
    return (
      // @ts-ignore
      "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0
    )
  }

  const btn = (
    <button
      type="button"
      css={css`
        all: unset;
        -webkit-text-size-adjust: 100%;
        margin: 0;
        text-transform: none;
        box-sizing: inherit;
        background: 0 0;
        border: 0;
        border-radius: 3px;
        color: inherit;
        cursor: pointer;
        flex-shrink: 0;
        overflow: visible;
        padding: 7px;
        position: relative;
        transition: all 0.3s ease;
        -webkit-appearance: button;
        font: inherit;
        line-height: inherit;
        width: auto;
        touch-action: manipulation;
        outline: 0;
        &:hover {
          background-color: #36b5f0;
        }
      `}
      onClick={e => {
        e.preventDefault()
        onClick()
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        x="0"
        y="0"
        enableBackground="new 0 0 263.374 263.374"
        version="1.1"
        viewBox="0 0 263.374 263.374"
        xmlSpace="preserve"
        css={css`
          all: unset;
          text-transform: none;
          color: inherit;
          cursor: pointer;
          font: inherit;
          line-height: inherit;
          box-sizing: inherit;
          overflow: hidden;
          display: block;
          fill: currentColor;
          height: 18px;
          pointer-events: none;
          width: 18px;
        `}
      >
        {icon === "play" && (
          <path d="M238.163 115.57l-68.127-39.741a29241.192 29241.192 0 00-55.296-32.256L44.115 3.831C28.919-5.067 13.974 2.07 13.974 19.698v224c0 17.567 14.945 24.735 30.147 15.872l69.376-39.741c15.232-8.863 40.735-23.357 55.936-32.256l68.449-39.741c15.165-8.899 15.452-23.399.281-32.262z" />
        )}
        {icon === "pause" && (
          <path d="M80 0H48C30.305 0 16 14.305 16 32v224c0 17.695 14.305 32 32 32h32c17.695 0 32-14.305 32-32V32c0-17.695-14.305-32-32-32zM240 0h-32c-17.695 0-32 14.305-32 32v224c0 17.695 14.305 32 32 32h32c17.695 0 32-14.305 32-32V32c0-17.695-14.305-32-32-32z" />
        )}
        {icon === "speaker" && (
          <React.Fragment>
            <path
              d="M126.5,44.3L75.2,82.3c-8.8,6.5-16,10.4-16,8.8c0-1.6-8.9-2.9-19.8-2.9H23.5c-10.9,0-19.8,8.9-19.8,19.8v43.6
	c0,10.9,8.9,19.8,19.8,19.8h15.9c10.9,0,19.8-0.5,19.8-1c0-0.6,7.2,4.3,16,10.7l51.3,37.9c8.8,6.4,16,2.9,16-8.1V52.4
	C142.5,41.5,135.3,37.9,126.5,44.3z"
            />
            <path
              d="M162.8,110l-10.4,10.1c3.8,2.6,6.4,6.7,6.4,11.6c0,4.9-2.6,9-6.4,11.6l10.4,10.1c6.2-5.2,10.2-13,10.2-21.7
	C173.1,123,169,115.2,162.8,110z"
            />
            <path
              d="M193.3,80.2l-10.2,10c11.2,10.4,18.3,25.1,18.3,41.6c0,16.5-7.1,31.2-18.3,41.6l10.2,10c13.7-13,22.3-31.2,22.3-51.5
	C215.7,111.4,207,93.1,193.3,80.2z"
            />
            <path
              d="M223.9,50.3l-10.2,10c18.7,18.1,30.4,43.4,30.4,71.4c0,28-11.7,53.3-30.4,71.4l10.2,10c21.2-20.6,34.4-49.5,34.4-81.4
	C258.3,99.8,245.1,71,223.9,50.3z"
            />
          </React.Fragment>
        )}
        {icon === "speaker-muted" && (
          <React.Fragment>
            <path
              d="M249.3,164.7c3,3,3,7.8,0,10.8c-1.5,1.5-3.4,2.2-5.4,2.2c-1.9,0-3.9-0.7-5.4-2.2l-33-33l-33,33c-1.5,1.5-3.4,2.2-5.4,2.2
	c-1.9,0-3.9-0.7-5.4-2.2c-3-3-3-7.8,0-10.8l33-33l-33-33c-3-3-3-7.8,0-10.8c3-3,7.8-3,10.8,0l33,33l33-33c3-3,7.8-3,10.8,0
	c3,3,3,7.8,0,10.8l-33,33L249.3,164.7z"
            />
            <path
              d="M126.5,44.3L75.2,82.3c-8.8,6.5-16,10.4-16,8.8c0-1.6-8.9-2.9-19.8-2.9H23.5c-10.9,0-19.8,8.9-19.8,19.8v43.6
	c0,10.9,8.9,19.8,19.8,19.8h15.9c10.9,0,19.8-0.5,19.8-1c0-0.6,7.2,4.3,16,10.7l51.3,37.9c8.8,6.4,16,2.9,16-8.1V52.4
	C142.5,41.5,135.3,37.9,126.5,44.3z"
            />
          </React.Fragment>
        )}
        {icon === "settings" && (
          <path
            d="M241.1,111.8l-26.3-6.5c-1.6-5-3.6-9.8-6.1-14.4c3.9-6.6,12.2-20.4,12.2-20.4c1.6-2.6,1.2-6-1-8.2l-19-19
			c-2.2-2.2-5.6-2.6-8.2-1l-20.4,12.2c-4.6-2.4-9.4-4.4-14.3-6l-6.5-26.3c-0.7-3-3.4-5.1-6.5-5.1h-26.8c-3.1,0-5.8,2.1-6.5,5.1
			c0,0-4.5,18.3-6.5,26.3c-5.3,1.7-10.4,3.8-15.2,6.5L68.9,42.3c-2.6-1.6-6-1.2-8.2,1l-19,19c-2.2,2.2-2.6,5.6-1,8.2l13.1,21.9
			c-2.1,4.2-3.9,8.5-5.3,12.9l-26.3,6.5c-3,0.7-5.1,3.4-5.1,6.5v26.8c0,3.1,2.1,5.8,5.1,6.5l26.3,6.5c1.6,5.1,3.7,10,6.2,14.7
			L43,192.3c-1.6,2.6-1.2,6,1,8.2l19,19c2.2,2.2,5.6,2.6,8.2,1c0,0,13.1-7.9,19.6-11.7c4.7,2.5,9.5,4.5,14.6,6.1l6.5,26.3
			c0.7,3,3.4,5.1,6.5,5.1h26.8c3.1,0,5.8-2.1,6.5-5.1l6.5-26.3c5.2-1.6,10.2-3.8,14.9-6.3c6.6,4,19.9,11.9,19.9,11.9
			c2.6,1.6,6,1.2,8.2-1l19-19c2.2-2.2,2.6-5.6,1-8.2L209,172.2c2.4-4.5,4.4-9.2,5.9-14.1l26.3-6.5c3-0.7,5.1-3.4,5.1-6.5v-26.8
			C246.2,115.2,244.1,112.5,241.1,111.8z M131.7,178.7c-25.9,0-47-21.1-47-47s21.1-47,47-47s47,21.1,47,47S157.6,178.7,131.7,178.7z
			"
          />
        )}
        {icon === "captions" && (
          <path
            d="M230.1,19.2H33.3c-15.5,0-28.1,12.6-28.1,28.1V216c0,15.5,12.6,28.1,28.1,28.1h196.8c15.5,0,28.1-12.6,28.1-28.1V47.3
			C258.2,31.8,245.6,19.2,230.1,19.2z M117.6,117.6H96.5v-7H68.4v42.2h28.1v-7h21.1v14.1c0,7.7-6.3,14.1-14.1,14.1H61.4
			c-7.8,0-14.1-6.3-14.1-14.1v-56.2c0-7.7,6.3-14.1,14.1-14.1h42.2c7.8,0,14.1,6.3,14.1,14.1L117.6,117.6L117.6,117.6z M216,117.6
			h-21.1v-7h-28.1v42.2h28.1v-7H216v14.1c0,7.7-6.3,14.1-14.1,14.1h-42.2c-7.8,0-14.1-6.3-14.1-14.1v-56.2c0-7.7,6.3-14.1,14.1-14.1
			H202c7.8,0,14.1,6.3,14.1,14.1V117.6z"
          />
        )}
        {icon === "captions-off" && (
          <path
            d="M230.1,19.2H33.3c-15.5,0-28.1,12.6-28.1,28.1V216c0,15.5,12.6,28.1,28.1,28.1h196.8c15.5,0,28.1-12.6,28.1-28.1V47.3
			C258.2,31.8,245.6,19.2,230.1,19.2z M117.6,117.6H96.5v-7H68.4v42.2h28.1v-7h21.1v14.1c0,7.7-6.3,14.1-14.1,14.1H61.4
			c-7.8,0-14.1-6.3-14.1-14.1v-56.2c0-7.7,6.3-14.1,14.1-14.1h42.2c7.8,0,14.1,6.3,14.1,14.1L117.6,117.6L117.6,117.6z M216,117.6
			h-21.1v-7h-28.1v42.2h28.1v-7H216v14.1c0,7.7-6.3,14.1-14.1,14.1h-42.2c-7.8,0-14.1-6.3-14.1-14.1v-56.2c0-7.7,6.3-14.1,14.1-14.1
			H202c7.8,0,14.1,6.3,14.1,14.1V117.6z"
            fill="rgba(255,255,255,0.5)"
          />
        )}
        {icon === "fullscreen" && (
          <g>
            <path
              d="M163,112.3L216,59.3v21.8c0,4.7,3.8,8.4,8.4,8.4c4.7,0,8.4-3.8,8.4-8.4V38.9c0-0.3,0-0.6,0-0.8c0-0.1,0-0.2-0.1-0.4
				c0-0.1,0-0.3-0.1-0.4c0-0.1-0.1-0.3-0.1-0.4c0-0.1-0.1-0.3-0.1-0.4c0-0.1-0.1-0.3-0.1-0.4c0-0.1-0.1-0.3-0.1-0.4
				s-0.1-0.2-0.2-0.4c-0.1-0.1-0.1-0.3-0.2-0.4c-0.1-0.1-0.1-0.2-0.2-0.3c-0.1-0.1-0.1-0.2-0.2-0.4c-0.1-0.1-0.2-0.2-0.3-0.4
				c-0.1-0.1-0.1-0.2-0.2-0.3c-0.2-0.2-0.4-0.4-0.6-0.6c0,0,0,0,0,0c0,0,0,0,0,0c-0.2-0.2-0.4-0.4-0.6-0.6c-0.1-0.1-0.2-0.1-0.3-0.2
				c-0.1-0.1-0.2-0.2-0.4-0.3c-0.1-0.1-0.2-0.1-0.4-0.2c-0.1-0.1-0.2-0.1-0.3-0.2c-0.1-0.1-0.3-0.1-0.4-0.2
				c-0.1-0.1-0.2-0.1-0.4-0.2c-0.1-0.1-0.3-0.1-0.4-0.1c-0.1,0-0.3-0.1-0.4-0.1c-0.1,0-0.3-0.1-0.4-0.1c-0.1,0-0.3-0.1-0.4-0.1
				c-0.1,0-0.3,0-0.4-0.1c-0.1,0-0.2,0-0.4-0.1c-0.3,0-0.6,0-0.8,0h-42.2c-4.7,0-8.4,3.8-8.4,8.4s3.8,8.4,8.4,8.4h21.8L151,100.4
				c-3.3,3.3-3.3,8.6,0,11.9C154.3,115.6,159.7,115.6,163,112.3z"
            />
            <path
              d="M100.4,151l-53.1,53.1v-21.8c0-4.7-3.8-8.4-8.4-8.4c-4.7,0-8.4,3.8-8.4,8.4v42.2c0,0.3,0,0.6,0,0.8c0,0.1,0,0.2,0.1,0.4
				c0,0.1,0,0.3,0.1,0.4c0,0.1,0.1,0.3,0.1,0.4c0,0.1,0.1,0.3,0.1,0.4c0,0.1,0.1,0.3,0.1,0.4c0,0.1,0.1,0.3,0.1,0.4
				c0.1,0.1,0.1,0.2,0.2,0.4c0.1,0.1,0.1,0.3,0.2,0.4c0.1,0.1,0.1,0.2,0.2,0.3c0.1,0.1,0.1,0.2,0.2,0.4c0.1,0.1,0.2,0.2,0.3,0.4
				c0.1,0.1,0.1,0.2,0.2,0.3c0.2,0.2,0.4,0.4,0.6,0.6c0,0,0,0,0,0c0,0,0,0,0,0c0.2,0.2,0.4,0.4,0.6,0.6c0.1,0.1,0.2,0.1,0.3,0.2
				c0.1,0.1,0.2,0.2,0.4,0.3c0.1,0.1,0.2,0.1,0.4,0.2c0.1,0.1,0.2,0.1,0.3,0.2c0.1,0.1,0.3,0.1,0.4,0.2c0.1,0.1,0.2,0.1,0.4,0.2
				c0.1,0.1,0.3,0.1,0.4,0.1c0.1,0,0.3,0.1,0.4,0.1c0.1,0,0.3,0.1,0.4,0.1c0.1,0,0.3,0.1,0.4,0.1c0.1,0,0.3,0,0.4,0.1
				c0.1,0,0.2,0,0.4,0.1c0.3,0,0.6,0,0.8,0h0h42.2c4.7,0,8.4-3.8,8.4-8.4c0-4.7-3.8-8.4-8.4-8.4H59.3l53.1-53.1
				c3.3-3.3,3.3-8.6,0-11.9C109.1,147.7,103.7,147.7,100.4,151z"
            />
            <path
              d="M231.2,229.5c0.1-0.1,0.2-0.2,0.3-0.4c0.1-0.1,0.2-0.2,0.2-0.4c0.1-0.1,0.1-0.2,0.2-0.3c0.1-0.1,0.1-0.3,0.2-0.4
				c0.1-0.1,0.1-0.2,0.2-0.4c0.1-0.1,0.1-0.3,0.1-0.4c0-0.1,0.1-0.3,0.1-0.4c0-0.1,0.1-0.3,0.1-0.4c0-0.1,0.1-0.3,0.1-0.4
				c0-0.1,0-0.3,0.1-0.4c0-0.1,0-0.2,0.1-0.4c0-0.3,0-0.6,0-0.8v-42.2c0-4.7-3.8-8.4-8.4-8.4c-4.7,0-8.4,3.8-8.4,8.4v21.8L163,151
				c-3.3-3.3-8.6-3.3-11.9,0c-3.3,3.3-3.3,8.6,0,11.9l53.1,53.1h-21.8c-4.7,0-8.4,3.8-8.4,8.4c0,4.7,3.8,8.4,8.4,8.4h42.2
				c0.3,0,0.6,0,0.8,0c0.1,0,0.2,0,0.4-0.1c0.1,0,0.3,0,0.4-0.1c0.1,0,0.3-0.1,0.4-0.1c0.1,0,0.3-0.1,0.4-0.1c0.1,0,0.3-0.1,0.4-0.1
				c0.1,0,0.3-0.1,0.4-0.1c0.1-0.1,0.2-0.1,0.4-0.2c0.1-0.1,0.3-0.1,0.4-0.2c0.1-0.1,0.2-0.1,0.3-0.2c0.1-0.1,0.2-0.1,0.4-0.2
				c0.1-0.1,0.2-0.2,0.4-0.3c0.1-0.1,0.2-0.1,0.3-0.2c0.2-0.2,0.4-0.4,0.6-0.6c0,0,0,0,0,0c0,0,0,0,0,0c0.2-0.2,0.4-0.4,0.6-0.6
				C231.1,229.7,231.1,229.6,231.2,229.5z"
            />
            <path
              d="M59.3,47.3h21.8c4.7,0,8.4-3.8,8.4-8.4c0-4.7-3.8-8.4-8.4-8.4H38.9h0c-0.3,0-0.6,0-0.8,0c-0.1,0-0.2,0-0.4,0.1
				c-0.1,0-0.3,0-0.4,0.1c-0.1,0-0.3,0.1-0.4,0.1c-0.1,0-0.3,0.1-0.4,0.1c-0.1,0-0.3,0.1-0.4,0.1c-0.1,0-0.3,0.1-0.4,0.1
				c-0.1,0.1-0.2,0.1-0.4,0.2c-0.1,0.1-0.3,0.1-0.4,0.2c-0.1,0.1-0.2,0.1-0.3,0.2c-0.1,0.1-0.2,0.1-0.4,0.2
				c-0.1,0.1-0.2,0.2-0.4,0.3c-0.1,0.1-0.2,0.1-0.3,0.2c-0.4,0.4-0.8,0.7-1.2,1.2c-0.1,0.1-0.1,0.2-0.2,0.3
				c-0.1,0.1-0.2,0.2-0.3,0.4c-0.1,0.1-0.1,0.2-0.2,0.4c-0.1,0.1-0.1,0.2-0.2,0.3c-0.1,0.1-0.1,0.3-0.2,0.4
				c-0.1,0.1-0.1,0.2-0.2,0.4c-0.1,0.1-0.1,0.3-0.1,0.4c0,0.1-0.1,0.3-0.1,0.4c0,0.1-0.1,0.3-0.1,0.4c0,0.1-0.1,0.3-0.1,0.4
				c0,0.1,0,0.3-0.1,0.4c0,0.1,0,0.2-0.1,0.4c0,0.3,0,0.6,0,0.8v42.2c0,4.7,3.8,8.4,8.4,8.4s8.4-3.8,8.4-8.4V59.3l53.1,53.1
				c3.3,3.3,8.6,3.3,11.9,0c3.3-3.3,3.3-8.6,0-11.9L59.3,47.3z"
            />
          </g>
        )}
        {icon === "exit-fullscreen" && (
          <g>
            <path d="M131.7,114.7c-9.4,0-17,7.6-17,17s7.6,17,17,17s17-7.6,17-17S141.1,114.7,131.7,114.7z" />
            <path
              d="M114.7,156.3c0-0.1,0-0.2-0.1-0.4c0-0.2,0-0.3-0.1-0.4c0-0.1-0.1-0.3-0.1-0.4c0-0.1-0.1-0.3-0.1-0.4
				c0-0.1-0.1-0.3-0.1-0.4c0-0.1-0.1-0.3-0.1-0.4c-0.1-0.1-0.1-0.2-0.2-0.4c-0.1-0.1-0.1-0.3-0.2-0.4c-0.1-0.1-0.1-0.2-0.2-0.3
				c-0.1-0.1-0.1-0.2-0.2-0.4c-0.1-0.1-0.2-0.2-0.3-0.4c-0.1-0.1-0.1-0.2-0.2-0.3c-0.2-0.2-0.4-0.4-0.6-0.6c0,0,0,0,0,0c0,0,0,0,0,0
				c-0.2-0.2-0.4-0.4-0.6-0.6c-0.1-0.1-0.2-0.2-0.3-0.2c-0.1-0.1-0.2-0.2-0.4-0.3c-0.1-0.1-0.2-0.2-0.4-0.2
				c-0.1-0.1-0.2-0.1-0.3-0.2c-0.1-0.1-0.3-0.1-0.4-0.2c-0.1-0.1-0.2-0.1-0.4-0.2c-0.1-0.1-0.3-0.1-0.4-0.1c-0.1,0-0.3-0.1-0.4-0.1
				c-0.1,0-0.3-0.1-0.4-0.1c-0.1,0-0.3-0.1-0.4-0.1c-0.1,0-0.3,0-0.4-0.1c-0.1,0-0.2,0-0.4-0.1c-0.3,0-0.6,0-0.8,0H63.7
				c-4.7,0-8.5,3.8-8.5,8.5s3.8,8.5,8.5,8.5h22l-53.4,53.4c-3.3,3.3-3.3,8.7,0,12c3.3,3.3,8.7,3.3,12,0l53.4-53.4v22
				c0,4.7,3.8,8.5,8.5,8.5s8.5-3.8,8.5-8.5v-42.5C114.7,156.9,114.7,156.6,114.7,156.3z"
            />
            <path
              d="M177.7,165.7h22c4.7,0,8.5-3.8,8.5-8.5s-3.8-8.5-8.5-8.5h-42.5c-0.3,0-0.6,0-0.8,0c-0.1,0-0.2,0-0.4,0.1
				c-0.2,0-0.3,0-0.4,0.1c-0.1,0-0.3,0.1-0.4,0.1c-0.1,0-0.3,0.1-0.4,0.1c-0.1,0-0.3,0.1-0.4,0.1c-0.1,0-0.3,0.1-0.4,0.1
				c-0.1,0.1-0.2,0.1-0.4,0.2c-0.1,0.1-0.3,0.1-0.4,0.2c-0.1,0.1-0.2,0.1-0.3,0.2c-0.1,0.1-0.2,0.1-0.4,0.2
				c-0.1,0.1-0.2,0.2-0.4,0.3c-0.1,0.1-0.2,0.1-0.3,0.2c-0.2,0.2-0.4,0.4-0.6,0.6c0,0,0,0,0,0c0,0,0,0,0,0c-0.2,0.2-0.4,0.4-0.6,0.6
				c-0.1,0.1-0.2,0.2-0.2,0.3c-0.1,0.1-0.2,0.2-0.3,0.4c-0.1,0.1-0.2,0.2-0.2,0.4c-0.1,0.1-0.1,0.2-0.2,0.3
				c-0.1,0.1-0.1,0.3-0.2,0.4c-0.1,0.1-0.1,0.2-0.2,0.4c-0.1,0.1-0.1,0.3-0.1,0.4c0,0.1-0.1,0.3-0.1,0.4c0,0.1-0.1,0.3-0.1,0.4
				c0,0.1-0.1,0.3-0.1,0.4c0,0.1,0,0.3-0.1,0.4c0,0.1,0,0.2-0.1,0.4c0,0.3,0,0.6,0,0.8v42.5c0,4.7,3.8,8.5,8.5,8.5
				c4.7,0,8.5-3.8,8.5-8.5v-22l53.4,53.4c3.3,3.3,8.7,3.3,12,0s3.3-8.7,0-12L177.7,165.7z"
            />
            <path
              d="M148.8,107.4c0,0.2,0,0.3,0.1,0.4c0,0.1,0.1,0.3,0.1,0.4c0,0.1,0.1,0.3,0.1,0.4c0,0.1,0.1,0.3,0.1,0.4
				c0,0.1,0.1,0.3,0.1,0.4c0.1,0.1,0.1,0.2,0.2,0.4c0.1,0.1,0.1,0.3,0.2,0.4c0.1,0.1,0.1,0.2,0.2,0.3c0.1,0.1,0.1,0.2,0.2,0.4
				c0.1,0.1,0.2,0.2,0.3,0.4c0.1,0.1,0.1,0.2,0.2,0.3c0.2,0.2,0.4,0.4,0.6,0.6c0,0,0,0,0,0s0,0,0,0c0.2,0.2,0.4,0.4,0.6,0.6
				c0.1,0.1,0.2,0.2,0.3,0.2c0.1,0.1,0.2,0.2,0.4,0.3c0.1,0.1,0.2,0.2,0.4,0.2c0.1,0.1,0.2,0.1,0.3,0.2c0.1,0.1,0.3,0.1,0.4,0.2
				c0.1,0.1,0.2,0.1,0.4,0.2c0.1,0.1,0.3,0.1,0.4,0.1c0.1,0,0.3,0.1,0.4,0.1c0.1,0,0.3,0.1,0.4,0.1c0.1,0,0.3,0.1,0.4,0.1
				c0.1,0,0.3,0,0.4,0.1c0.1,0,0.2,0,0.4,0.1c0.3,0,0.6,0,0.8,0h42.5c4.7,0,8.5-3.8,8.5-8.5s-3.8-8.5-8.5-8.5h-22l53.4-53.4
				c3.3-3.3,3.3-8.7,0-12c-3.3-3.3-8.7-3.3-12,0l-53.4,53.4v-22c0-4.7-3.8-8.5-8.5-8.5s-8.5,3.8-8.5,8.5v42.5c0,0.3,0,0.6,0,0.8
				C148.7,107.2,148.8,107.3,148.8,107.4z"
            />
            <path
              d="M85.7,97.7h-22c-4.7,0-8.5,3.8-8.5,8.5c0,4.7,3.8,8.5,8.5,8.5h42.5c0.3,0,0.6,0,0.8,0c0.1,0,0.2,0,0.4-0.1
				c0.2,0,0.3,0,0.4-0.1c0.1,0,0.3-0.1,0.4-0.1c0.1,0,0.3-0.1,0.4-0.1c0.1,0,0.3-0.1,0.4-0.1c0.1,0,0.3-0.1,0.4-0.1
				c0.1-0.1,0.2-0.1,0.4-0.2c0.1-0.1,0.3-0.1,0.4-0.2c0.1-0.1,0.2-0.1,0.3-0.2c0.1-0.1,0.2-0.1,0.4-0.2c0.1-0.1,0.2-0.2,0.4-0.3
				c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.4,0.8-0.8,1.2-1.2c0.1-0.1,0.2-0.2,0.2-0.3c0.1-0.1,0.2-0.2,0.3-0.4c0.1-0.1,0.2-0.2,0.2-0.4
				c0.1-0.1,0.1-0.2,0.2-0.3c0.1-0.1,0.1-0.3,0.2-0.4c0.1-0.1,0.1-0.2,0.2-0.4c0.1-0.1,0.1-0.3,0.1-0.4c0-0.1,0.1-0.3,0.1-0.4
				c0-0.1,0.1-0.3,0.1-0.4c0-0.1,0.1-0.3,0.1-0.4c0-0.1,0-0.3,0.1-0.4c0-0.1,0-0.2,0.1-0.4c0-0.3,0-0.6,0-0.8V63.7
				c0-4.7-3.8-8.5-8.5-8.5c-4.7,0-8.5,3.8-8.5,8.5v22L44.3,32.3c-3.3-3.3-8.7-3.3-12,0c-3.3,3.3-3.3,8.7,0,12L85.7,97.7z"
            />
          </g>
        )}
      </svg>
    </button>
  )
  if (!displayToolTips || isTouchDevice()) {
    return btn
  }
  return (
    <div>
      <ToolTip content={<span>{tooltip.text}</span>} placement={tooltip.placement}>
        {btn}
      </ToolTip>
    </div>
  )
}
